import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const team = [
  {
    name: 'Yosef Puetate',
    role: 'Presidente',
    specialty: 'Liderazgo estudiantil y estrategia tecnológica',
    image: '/assets/IEEE_fotos_sin_fondo/presidente.png',
    alt: 'Yosef Puetate',
  },
  {
    name: 'Camilo Martillo',
    role: 'Vicepresidente',
    specialty: 'Gestión de proyectos y alianzas',
    image: '/assets/IEEE_fotos_sin_fondo/vicepresidente.png',
    alt: 'Camilo Martillo',
  },
  {
    name: 'Nahomi Tayupanta',
    role: 'Secretaria',
    specialty: 'Coordinación académica y comunicaciones',
    image: '/assets/IEEE_fotos_sin_fondo/secretaria.png',
    alt: 'Nahomi Tayupanta',
  },
  {
    name: 'Amelie Ganchozo',
    role: 'Tesorera',
    specialty: 'Administración y finanzas estudiantiles',
    image: '/assets/IEEE_fotos_sin_fondo/tesorera.png',
    alt: 'Amelie Ganchozo',
  },
  {
    name: 'Scarleth Monar',
    role: 'Coordinadora de Membresías',
    specialty: 'Reclutamiento y retención de miembros',
    image: '/assets/IEEE_fotos_sin_fondo/coordinadora_de_membresias.png',
    alt: 'Scarleth Monar',
    hasQR: true,
  },
  {
    name: 'Mateo Suarez',
    role: 'Web Master',
    specialty: 'Diseño digital y presencia online',
    image: '/assets/IEEE_fotos_sin_fondo/web_master.png',
    alt: 'Mateo Suarez',
  },
];

const Team: React.FC = () => {
  const [selectedMember, setSelectedMember] = useState<typeof team[0] | null>(null);

  return (
    <section className="relative overflow-hidden bg-white py-24" id="team">
      {/* Light soft gradients */}
      <div className="absolute left-0 top-10 h-[500px] w-[500px] rounded-full bg-indigo-50/40 blur-[120px] pointer-events-none" />
      <div className="absolute right-0 bottom-10 h-[600px] w-[600px] rounded-full bg-cyan-50/40 blur-[120px] pointer-events-none" />
      
      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: '-50px' }}
          className="text-center mb-20 space-y-4"
        >
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-xl bg-indigo-50 border border-indigo-100 shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.4)]"></span>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600">Directiva 2026</span>
          </div>
          <h2 className="text-4xl font-black text-slate-900 sm:text-5xl tracking-tight">
            El equipo que impulsa <span className="text-indigo-600">IEEE ESPOCH</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-slate-600 font-normal">
            Liderazgo estratégico con visión técnica e institucional para la ingeniería del futuro.
          </p>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {team.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 35 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: index * 0.08 }}
              viewport={{ once: true, margin: '-50px' }}
              whileHover={{ y: -8 }}
              onClick={() => member.hasQR && setSelectedMember(member)}
              className={`group overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:border-indigo-100 hover:shadow-premium ${member.hasQR ? 'cursor-pointer' : ''}`}
            >
              <div className="relative overflow-hidden bg-slate-50">
                <div className="aspect-[4/5] overflow-hidden flex items-end justify-center bg-gradient-to-t from-slate-100 to-transparent pt-8">
                  <img
                    src={member.image}
                    alt={member.alt}
                    className="h-[90%] w-auto object-cover object-bottom transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Subtle blend overlay */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-200/50 to-transparent mix-blend-multiply" />
                </div>
                {member.hasQR && (
                  <div className="absolute top-6 right-6 h-10 w-10 rounded-full bg-white/80 backdrop-blur-md flex items-center justify-center border border-indigo-100 shadow-sm text-indigo-600">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M7 7h1M11 7h1M15 7h2M7 11h1M11 11h1M15 11h2M7 15h2M11 15h1M15 15h2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="space-y-4 p-8 bg-white relative">
                <div className="space-y-1">
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-600">{member.role}</p>
                  <h3 className="text-2xl font-black text-slate-900">{member.name}</h3>
                </div>
                <div className="h-px w-8 bg-slate-200 transition-all duration-300 group-hover:w-full group-hover:bg-indigo-100" />
                <p className="text-sm text-slate-500 leading-relaxed font-normal">{member.specialty}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal for Membership Coordinator */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedMember(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-white/95 backdrop-blur-xl p-4 sm:p-8"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl bg-white rounded-[2.5rem] border border-slate-100 shadow-premium overflow-hidden"
            >
              <button
                onClick={() => setSelectedMember(null)}
                className="absolute top-6 right-6 z-20 h-12 w-12 rounded-xl bg-slate-50 text-slate-900 flex items-center justify-center border border-slate-100 hover:bg-slate-100 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>

              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/2 bg-slate-50 aspect-[4/5] lg:aspect-auto overflow-hidden flex items-end justify-center">
                  <img
                    src={selectedMember.image}
                    alt={selectedMember.alt}
                    className="h-[90%] w-auto object-cover object-bottom"
                  />
                </div>
                <div className="lg:w-1/2 p-8 sm:p-12 space-y-8 flex flex-col justify-center">
                  <div className="space-y-4">
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-600">{selectedMember.role}</p>
                    <h3 className="text-4xl font-black text-slate-900 leading-tight">{selectedMember.name}</h3>
                    <p className="text-lg text-slate-600 leading-relaxed font-normal">{selectedMember.specialty}</p>
                  </div>
                  
                  <div className="pt-8 border-t border-slate-100 space-y-6 text-center lg:text-left">
                    <p className="text-sm font-bold text-slate-900 uppercase tracking-[0.1em]">Escanea el código para unirte</p>
                    <div className="mx-auto lg:mx-0 w-48 h-48 p-4 bg-white rounded-2xl border border-indigo-100 shadow-sm flex items-center justify-center">
                      <img
                        src="/assets/qr_coordinadora.png"
                        alt="Código QR de Membresías"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Team;
